import {Control, FieldValues, UseFormReset} from 'react-hook-form';

export type ValidationErrors = ValidationError[];
export type ValidationError = {
  name: string;
  message: string;
};

export type BlockerContext = {
  /**
   * True when the submit is triggered from the UnsavedChangesBlocker "Save and Continue" dialog.
   * Check this to skip your own `navigate()` call — the blocker handles navigation.
   */
  wasSubmittedFromBlocker?: boolean;
  /**
   * Call this when an API error occurs to prevent the blocker from navigating away.
   * The toast notification from your error handler is still shown as normal.
   *
   * @example
   * const handleSubmit: FormSubmitHandler<MyForm> = async (values, setErrors, reset, blockerContext) => {
   *   await mutation().unwrap()
   *     .then(() => showNotification.success())
   *     .catch((e: ApiError) => {
   *       handleApiError(e);
   *       blockerContext?.reportApiFailure?.();
   *     });
   * };
   */
  reportApiFailure?: VoidFunction;
};

export type FormSubmitHandler<
  TFieldValues extends FieldValues = FieldValues,
  TPromiseReturn = unknown,
> = (
  values: TFieldValues,
  setErrors: (errors: ValidationErrors | null) => void,
  reset: UseFormReset<TFieldValues>,
  blockerContext?: BlockerContext
) => Promise<TPromiseReturn>;

export type FormControl<TFieldValues extends FieldValues = FieldValues> = Control<TFieldValues> & {
  formId: string;
  onChange: VoidFunction;
  submitForm: (
    blockerContext?: Pick<BlockerContext, 'wasSubmittedFromBlocker'>
  ) => Promise<boolean>;
};
